// deps
import { useCallback, useMemo } from "react";

// interfaces
import {
    SwiggyInstrumentsData,
    SwiggyInstrumentsPaymentGroupActions,
    SwiggyInstrumentsPaymentMethodData,
    SwiggyInstrumentsSpecificActions,
} from "@swiggy-private/payment-ui";
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// hooks
import { usePaymentActions } from "./UsePaymentActions";
import { useSwiggyMoneyActions } from "@payment-client/hooks/UseSwiggyMoneyActions";

interface ReturnType {
    paymentActions: SwiggyInstrumentsPaymentGroupActions;
    swiggyInstrumentsSpecificActions: SwiggyInstrumentsSpecificActions;
}

const getAdditionalBalance = (paymentAmount: number, currentBalance: number): number => paymentAmount - currentBalance;

interface UseSwiggyInstrumentsHandlerProps {
    onPay: PaymentGroupHandlerProps["onPay"];
    onSelect: PaymentGroupHandlerProps["onSelect"];
    paymentAmount: string;
}

export const useSwiggyInstrumentsHandler = (props: UseSwiggyInstrumentsHandlerProps): ReturnType => {
    const { onSelect, onPay, paymentAmount } = props;
    const actions = usePaymentActions({ onSelect, onPay });
    const { addMoneyActions, swiggyMoneyInfoActions } = useSwiggyMoneyActions();
    const getSwiggyInstrumentsData = useCallback(
        (methodData: SwiggyInstrumentsPaymentMethodData): SwiggyInstrumentsData => {
            const additionalBalance = getAdditionalBalance(Number(paymentAmount), Number(methodData.eligibleBalance));
            return {
                eligibleBalance: methodData.eligibleBalance,
                balance: methodData.balance,
                additionalBalanceRequired: !isNaN(additionalBalance) ? String(additionalBalance) : "",
                isBalanceSufficient: isNaN(additionalBalance) || additionalBalance <= 0,
            };
        },
        [paymentAmount],
    );

    const swiggyInstrumentsSpecificActions = useMemo(
        (): SwiggyInstrumentsSpecificActions => ({
            getSwiggyInstrumentsData,
            swiggyMoneyInfoActions,
            ...addMoneyActions,
        }),
        [getSwiggyInstrumentsData, swiggyMoneyInfoActions, addMoneyActions],
    );

    const paymentActions = useMemo(
        (): SwiggyInstrumentsPaymentGroupActions => ({
            ...actions,
            ...swiggyInstrumentsSpecificActions,
        }),
        [actions, swiggyInstrumentsSpecificActions],
    );

    return { paymentActions, swiggyInstrumentsSpecificActions };
};
